import 'dayjs/locale/ru';

import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

import i18n from '../i18n/i18n';
import { ISO_DATE_ONLY, ISO_WITHOUT_TIMEZONE } from './constants';
import type { DateType, IsoStringOptions, UnitType } from './types';

dayjs.extend(weekday);
dayjs.extend(advanced);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(timezone);
dayjs.extend(utc);

export class DateService {
  static resolveLocale(locale?: string) {
    return locale || i18n.language || 'ru';
  }

  static format(date: DateType, template: string, locale = i18n.language, capitalize = false) {
    const resolvedLocale = this.resolveLocale(locale);
    let formattedDate = dayjs(date).locale(resolvedLocale).format(template);

    if (capitalize) {
      formattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    }

    return formattedDate;
  }

  static formatRaw(date: DateType, template: string, locale = i18n.language, capitalize = false) {
    const resolvedLocale = this.resolveLocale(locale);
    let formattedDate = dayjs.utc(date).locale(resolvedLocale).format(template);

    if (capitalize) {
      formattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    }

    return formattedDate;
  }

  static formattedDateWithOptionalYear(dateFrom: DateType, days: number, locale = i18n.language) {
    const shouldDisplayYear = !this.isSame(dateFrom, dayjs(dateFrom).add(days - 1, 'days'), 'year');

    return [...Array(days)].map((_, index) => {
      const currentDate = dayjs(dateFrom).add(index, 'days');
      const displayFormat = shouldDisplayYear ? 'DD MMMM, YYYY' : 'DD MMMM, dd';
      return this.format(currentDate, displayFormat, locale);
    });
  }

  static formatWithTimezone(
    date: DateType,
    timezone: string,
    template: string,
    locale = i18n.language,
  ) {
    const resolvedLocale = this.resolveLocale(locale);
    return dayjs(date).locale(resolvedLocale).tz(timezone).format(template);
  }

  static parse(date: DateType, format?: string, locale = i18n.language, strict = false) {
    const resolvedLocale = this.resolveLocale(locale);
    return dayjs(date, format, resolvedLocale, strict);
  }

  static formatRange(
    dateFrom: DateType,
    dateTo: DateType,
    template: string,
    locale = i18n.language,
    capitalize = false,
  ) {
    const formattedFrom = this.format(dateFrom, template, locale, capitalize);
    const formattedTo = this.format(dateTo, template, locale, capitalize);

    if (this.isSame(dateFrom, dateTo)) {
      return formattedFrom;
    }

    return `${formattedFrom} - ${formattedTo}`;
  }

  static toISOString(date: DateType, options: IsoStringOptions = {}) {
    if (options.dateOnly) {
      return dayjs(date).format(ISO_DATE_ONLY);
    }
    if (options.ignoreTimezone) {
      return dayjs(date).format(ISO_WITHOUT_TIMEZONE);
    }
    return dayjs(date).toISOString();
  }

  static normalizeToISO(date: string | number | null): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string' && /^\d+$/.test(date)) {
      return dayjs(Number(date)).toISOString();
    }

    return dayjs(date).toISOString();
  }

  static diff(date1: DateType, date2: DateType, unit?: UnitType) {
    return dayjs(date1).diff(date2, unit);
  }

  static isSame(date1: DateType, date2: DateType, unit?: UnitType) {
    return dayjs(date1).isSame(date2, unit);
  }

  static isBefore(date1: DateType, date2: DateType, unit?: UnitType) {
    return dayjs(date1).isBefore(date2, unit);
  }

  static isToday(date: DateType) {
    return dayjs(date).isToday();
  }

  static isYesterday(date: DateType) {
    return dayjs(date).isYesterday();
  }

  static isLastWeek(date: DateType) {
    const start = dayjs().subtract(7, 'days');
    const end = dayjs();

    return dayjs(date).isBetween(start, end);
  }

  static isCurrentYear(date: DateType) {
    return dayjs(date).year() === dayjs().year();
  }

  static calendarTime(date: DateType) {
    if (this.isToday(date)) {
      return this.format(date, 'HH:mm');
    }

    if (this.isLastWeek(date)) {
      return this.format(date, 'dd');
    }

    if (this.isCurrentYear(date)) {
      return this.format(date, 'D MMM');
    }

    return this.format(date, 'DD.MM.YY');
  }

  static calendarDay(date: DateType) {
    if (this.isToday(date)) {
      return i18n.t('common.date.today');
    }

    if (this.isYesterday(date)) {
      return i18n.t('common.date.yesterday');
    }

    if (this.isCurrentYear(date)) {
      return this.format(date, 'D MMMM');
    }

    return this.format(date, 'D MMMM YYYY');
  }

  static getCurrentTimezone(): string {
    return dayjs.tz.guess();
  }

  static getStartOfWeek(date: DateType, offset: number = 0) {
    return this.parse(this.format(date, 'YYYY-MM-DD'), 'YYYY-MM-DD')
      .subtract(offset, 'week')
      .startOf('week')
      .add(1, 'day')
      .set('hours', 0)
      .set('minutes', 0)
      .set('seconds', 0)
      .utc()
      .format();
  }

  static getEndOfWeek(date: DateType, offset: number = 0) {
    return this.parse(this.format(date, 'YYYY-MM-DD'), 'YYYY-MM-DD')
      .subtract(offset, 'week')
      .endOf('week')
      .add(1, 'day')
      .set('hours', 23)
      .set('minutes', 59)
      .set('seconds', 59)
      .utc()
      .format();
  }

  static getRelativeTimeStamp(timestamp: DateType) {
    const currentDateAndTime = dayjs();
    const timeStamp = dayjs(timestamp);

    const formattedDate = timeStamp
      .locale(this.resolveLocale())
      .format('D MMMM YYYY, HH:mm');

    const diffInSeconds = this.diff(currentDateAndTime, timeStamp, 'second');
    const diffInMinutes = this.diff(currentDateAndTime, timeStamp, 'minute');
    const diffInHours = this.diff(currentDateAndTime, timeStamp, 'hour');
    const diffInDays = this.diff(currentDateAndTime, timeStamp, 'day');
    const diffInYears = this.diff(currentDateAndTime, timeStamp, 'year');

    let relativeTime;

    if (diffInSeconds < 60) {
      relativeTime = `${this.format(timeStamp, 'HH:mm')}`;
    }

    if (diffInMinutes >= 1 && diffInMinutes < 60) {
      relativeTime = `${diffInMinutes} ${i18n.t('common.date.minutes')}`;
    }

    if (diffInHours >= 1 && diffInHours < 24) {
      relativeTime = `${diffInHours} ${i18n.t('common.date.hours')}`;
    }

    if (diffInDays >= 1 && diffInDays < dayjs().daysInMonth()) {
      relativeTime = `${diffInDays} ${i18n.t('common.date.days')}`;
    }

    if (diffInDays >= dayjs().daysInMonth()) {
      relativeTime = `${Math.floor(diffInDays / dayjs().daysInMonth())} ${i18n.t('common.date.months')}`;
    }

    if (diffInYears >= 1) {
      let yearLabel;

      if (diffInYears === 1) {
        yearLabel = i18n.t('common.date.year');
      }

      if (diffInYears >= 2 && diffInYears <= 4) {
        yearLabel = i18n.t('common.date.fewYears');
      }

      if (diffInYears > 4) {
        yearLabel = i18n.t('common.date.manyYears');
      }

      relativeTime = `${diffInYears} ${yearLabel}`;
    }

    return { relativeTime, formattedDate, diffInSeconds };
  }

  static getRelativeTimeDiff(diffInSeconds: number) {
    let relativeTime;

    if (diffInSeconds < 60) {
      relativeTime = i18n.t('common.date.now');
    }

    if (diffInSeconds >= 60 && diffInSeconds < 60 * 60) {
      relativeTime = `${Math.floor(diffInSeconds / 60)} ${i18n.t('common.date.minutes')}`;
    }

    if (diffInSeconds >= 60 * 60 && diffInSeconds < 24 * 60 * 60) {
      relativeTime = `${Math.floor(diffInSeconds / (60 * 60))} ${i18n.t('common.date.hours')}`;
    }

    if (diffInSeconds >= 24 * 60 * 60 && diffInSeconds < dayjs().daysInMonth() * 24 * 60 * 60) {
      relativeTime = `${Math.floor(diffInSeconds / (24 * 60 * 60))} ${i18n.t('common.date.days')}`;
    }

    if (
      diffInSeconds >= dayjs().daysInMonth() * 24 * 60 * 60 &&
      diffInSeconds < 12 * dayjs().daysInMonth() * 24 * 60 * 60
    ) {
      relativeTime = `${Math.floor(diffInSeconds / (dayjs().daysInMonth() * 24 * 60 * 60))} ${i18n.t('common.date.months')}`;
    }

    if (diffInSeconds >= 12 * dayjs().daysInMonth() * 24 * 60 * 60) {
      const year = Math.floor(diffInSeconds / (12 * dayjs().daysInMonth() * 24 * 60 * 60));
      relativeTime = `${i18n.t('common.date.year', { count: year })}`;
    }

    return { relativeTime };
  }

  static clone(date: DateType): DateType {
    return dayjs(date).clone();
  }

  static startOfWeek(date: DateType): DateType {
    return dayjs(date).startOf('week');
  }

  static endOfWeek(date: DateType): DateType {
    return dayjs(date).endOf('week');
  }

  static startOfMonth(date: DateType): DateType {
    return dayjs(date).startOf('month');
  }

  static endOfMonth(date: DateType): DateType {
    return dayjs(date).endOf('month');
  }

  static addDays(date: DateType, amount: number): DateType {
    return dayjs(date).add(amount, 'day');
  }

  static addWeeks(date: DateType, amount: number): DateType {
    return dayjs(date).add(amount, 'week');
  }

  static addMonths(date: DateType, amount: number): DateType {
    return dayjs(date).add(amount, 'month');
  }

  static isAfter(date1: DateType, date2: DateType, unit?: UnitType): boolean {
    return dayjs(date1).isAfter(date2, unit);
  }

  static isSameDay(date1: DateType, date2: DateType): boolean {
    return dayjs(date1).isSame(date2, 'day');
  }
}
